import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tag,
  Plus,
  Edit3,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
} from "lucide-react";
import { feeItemsApi, PaginatedResponse } from "../../services/api";
import { useNavigate } from "react-router-dom";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";

interface FeeItem {
  _id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  defaultPrice: { amountCents: number; currency: string };
  defaultQuantity: number;
  tax: { taxable: boolean; taxCode: string | null };
  active: boolean;
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const categoryColors: Record<string, string> = {
  legal_fee: "bg-emerald-100 text-emerald-700",
  disbursement: "bg-blue-100 text-blue-700",
  admin: "bg-purple-100 text-purple-700",
  tax: "bg-amber-100 text-amber-700",
  other: "bg-slate-100 text-slate-600",
};

const formatCategory = (c: string) =>
  c
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const FeeItemsPage: React.FC = () => {
  const navigate = useNavigate();
  const [feeItems, setFeeItems] = useState<FeeItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<FeeItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    category: "legal_fee",
    amountCents: 0,
    defaultQuantity: 1,
    taxable: true,
  });
  const [categories, setCategories] = useState<string[]>([
    "legal_fee", "disbursement", "admin", "tax", "other",
  ]);

  const fetchFeeItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (search) params.search = search;
      if (filterCategory) params.category = filterCategory;

      const res = await feeItemsApi.list(params);
      const data = res.data as PaginatedResponse<FeeItem>;
      setFeeItems(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch fee items:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, filterCategory]);

  useEffect(() => {
    fetchFeeItems();
  }, [fetchFeeItems]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    feeItemsApi.categories().then((res) => {
      if (Array.isArray(res.data) && res.data.length > 0) {
        setCategories(res.data);
      }
    }).catch(console.error);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      code: "",
      name: "",
      description: "",
      category: "legal_fee",
      amountCents: 0,
      defaultQuantity: 1,
      taxable: true,
    });
    setShowModal(true);
  };

  const openEdit = (item: FeeItem) => {
    setEditing(item);
    setForm({
      code: item.code,
      name: item.name,
      description: item.description || "",
      category: item.category,
      amountCents: item.defaultPrice.amountCents,
      defaultQuantity: item.defaultQuantity,
      taxable: item.tax?.taxable ?? true,
    });
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        code: form.code,
        name: form.name,
        description: form.description || null,
        category: form.category,
        defaultPrice: {
          amountCents: form.amountCents,
          currency: "CAD",
        },
        defaultQuantity: form.defaultQuantity,
        tax: {
          taxable: form.taxable,
        },
      };

      if (editing) {
        await feeItemsApi.update(editing._id, data);
      } else {
        await feeItemsApi.create(data);
      }
      setShowModal(false);
      fetchFeeItems();
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to save fee item");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Deactivate this fee item?")) return;
    try {
      await feeItemsApi.remove(id);
      fetchFeeItems();
    } catch (err) {
      console.error("Failed to deactivate:", err);
    }
  };

  // Group by category
  const grouped = feeItems.reduce<Record<string, FeeItem[]>>((acc, item) => {
    const cat = item.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/billing")}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Fee Items Catalog</h1>
            <p className="text-sm text-slate-500 mt-1">
              {pagination.total} fee item{pagination.total !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-5 w-5" />
          New Fee Item
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search fee items..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <SearchableSelect
            options={categories.map((c) => ({ value: c, label: formatCategory(c) }))}
            value={filterCategory || null}
            onChange={(val) => {
              setFilterCategory(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Categories"
            clearable
          />
        </div>
      </div>

      {/* Fee Items */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : feeItems.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20 text-slate-500">
          <Tag className="h-12 w-12 mb-3 text-slate-300" />
          <p className="font-medium text-lg">No fee items yet</p>
          <p className="text-sm mt-1">Create fee items to use in invoices</p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
            Create Fee Item
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    categoryColors[category] || "bg-slate-100 text-slate-600"
                  }`}
                >
                  {formatCategory(category)}
                </span>
                <span className="text-slate-400">({items.length})</span>
              </h3>
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="text-right px-6 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell">
                        Default Price
                      </th>
                      <th className="text-center px-6 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wider hidden md:table-cell">
                        Taxable
                      </th>
                      <th className="text-right px-6 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <motion.tr
                        key={item._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-3 text-sm font-mono text-slate-500">
                          {item.code}
                        </td>
                        <td className="px-6 py-3">
                          <p className="text-sm font-medium text-slate-800">{item.name}</p>
                          {item.description && (
                            <p className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">
                              {item.description}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-3 text-sm font-semibold text-slate-800 text-right hidden sm:table-cell">
                          {formatCurrency(item.defaultPrice.amountCents)}
                        </td>
                        <td className="px-6 py-3 text-center hidden md:table-cell">
                          {item.tax?.taxable ? (
                            <span className="text-emerald-600 text-xs font-medium">Yes</span>
                          ) : (
                            <span className="text-slate-400 text-xs">No</span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEdit(item)}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                              title="Edit"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeactivate(item._id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"
                              title="Deactivate"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Modal */}
      <PremiumModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Fee Item" : "New Fee Item"}
        icon={Tag}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="feeItemForm"
              disabled={saving || !form.code || !form.name}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Create Fee Item"}
            </button>
          </div>
        }
      >
        <form id="feeItemForm" onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
              <input
                type="text"
                required
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                placeholder="IMM_TRV_PKG"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Category *
              </label>
              <SearchableSelect
                options={categories.map((c) => ({ value: c, label: formatCategory(c) }))}
                value={form.category}
                onChange={(val) => setForm({ ...form, category: val || "legal_fee" })}
                placeholder="Select category"
                addNew={{
                  label: "New Category",
                  onAdd: () => {
                    const name = window.prompt("New category name (e.g. consulting_fee):");
                    if (name && name.trim()) {
                      const cat = name.trim().toLowerCase().replace(/\s+/g, "_");
                      if (!categories.includes(cat)) {
                        setCategories((prev) => [...prev, cat].sort());
                      }
                      setForm({ ...form, category: cat });
                    }
                  },
                }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Temporary Resident Visa Package"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Default Price ($) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={form.amountCents ? form.amountCents / 100 : ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    amountCents: Math.round(parseFloat(e.target.value || "0") * 100),
                  })
                }
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                placeholder="500.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Default Qty
              </label>
              <input
                type="number"
                min="1"
                value={form.defaultQuantity}
                onChange={(e) =>
                  setForm({ ...form, defaultQuantity: parseInt(e.target.value) || 1 })
                }
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="taxable"
              checked={form.taxable}
              onChange={(e) => setForm({ ...form, taxable: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="taxable" className="text-sm font-medium text-slate-700">
              Taxable
            </label>
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default FeeItemsPage;
